import toast from "utils/toast";

/**
 * Services
 */
import getApiUrl from "services/getApiUrl";
import convertToIndex from "components/Utils/convertToIndex";
import _ from "lodash";
import axios from "axios";
import { trackFormActivity } from "services/helper/formActivity";

/**
 * Monta o payload de service_update para rename de campo (espelha
 * buildServiceUpdateField do back-end). Retorna null se faltar info.
 */
const buildServiceUpdateField = ({ author, idField, oldFieldName, newFieldName }) => {
  if (!idField || !newFieldName || !oldFieldName || !author) {
    return null;
  }
  return {
    author,
    idField,
    oldFieldName: typeof oldFieldName === "object" ? oldFieldName.field : oldFieldName,
    newFieldName,
  };
};

/**
 * Cria formulário.
 * @param {*} items
 * @returns response
 */
export const addObject = async (items) => {
  const { formName, createdBy, companyId, unitId, categories = [], description = '', } = items;
  return await getApiUrl
    .post(`/objetos/novo`, {
      formName,
      categories,
      description,
      createdBy,
      companyId,
      unitId
    })
    .then(async (response) => {
      if (response.data?.success) {
        await trackFormActivity({
          form: formName,
          entity: "form",
          action: "created",
          payload: {
            description,
            configSync: { value: 0, valueType: "", fullSync: true },
            fieldDateMain: "",
          },
          createdBy,
          metadata: { formId: response.data.formId, companyId, unitId, userUuid: createdBy },
        });
      }
      return response.data;
    })
    .catch((err) => {
      return err.response.data;
    });
};

/**
 * Busca por formulário existente.
 * @param {*} items
 * @returns
 */
export const findObject = async (items) => {
  const { name } = items;
  return await getApiUrl
    .post(`/objetos/pesquisar`, {
      name,
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.log(err);
      return null;
    });
};

/**
 * Lista de todos formulários sem filtro.
 * @returns response
 */
export const allObjects = async () => {
  try {
    const response = await getApiUrl.get('/objetos/todos');
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    throw error;
  }
};

/**
 * Lista de todos formulários com filtro ${name}.
 * @returns response
 */
export const allFormsOfSavedObjects = async (formName = null, unitId = null) => {
  let url = '/objetos/formularios/dados/todos';
  const params = new URLSearchParams();

  if (formName) {
    params.append('formName', formName);
  }
  if (unitId) {
    params.append('unitId', unitId);
  }

  try {
    const { data } = await getApiUrl.get(url, {
      params
    }); // Usa a instância do Axios
    return data;
  } catch (error) {
    if (error.response && error.response.data) {
      return error.response || error;
    }
    throw error;
  }
}


export const allFormsOfSavedObjectsPagination = async ({
  formName = null,
  unitId = null,
  pageSize = 20,
  page = 1,
}) => {
  const url = '/objetos/formularios/dados/todos/paginado';

  const params = new URLSearchParams();

  if (formName) {
    params.append('formName', formName);
  }
  if (unitId) {
    params.append('unitId', unitId);
  }
  params.append('pageSize', pageSize);
  params.append('page', page);

  try {

    const { data } = await getApiUrl.get(url, { params });
    return data;
  } catch (error) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    throw error;
  }
};


/**
 * Lista de todos formulários com campos.
 * @returns response
 */
export const allFormsOfSavedObjectsWithFields = async () => {
  try {
    const response = await getApiUrl.get('/objetos/formularios/categorias/dados/todos'); // Usa a instância do Axios
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    throw error;
  }
};

export const allFormsOfSavedObjectsWithFieldsSearch = async (categories) => {
  return await getApiUrl
    .post(`/objetos/formularios/categorias/dados/pesquisar`, {
      categories,
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.log(err);
      return null;
    });
};

/**
 * Adiciona novo campo ao formulário.
 * @param {*} objects
 * @returns response
 */
export const addFields = async (objects) => {
  const { formName, fields, createdBy, companyId, unitId, formId } = objects;
  return await getApiUrl
    .post(`/objetos/campos/novo`, {
      formName,
      fields,
      createdBy
    })
    .then(async () => {
      toast({
        title: "Adição",
        position: "top-right",
        description: `Campo(s) adicionado com sucesso no formulário: ${convertToIndex(formName.replace("obj_", ""))}`,
        isClosable: false,
        status: "success",
        duration: 3500,
      });
      for (const field of fields) {
        await trackFormActivity({
          form: formName.replace("obj_", ""),
          entity: "field",
          action: "created",
          payload: {
            checkBox: field.checkBox,
            field: field.field,
            dica: field.dica,
            sufixo: field.sufixo,
            fieldtype: field.fieldtype,
            autoFill: field.autoFill,
            requiredField: field.requiredField,
            index: field.index ?? null,
            marker: field.marker,
            formula: field.formula ?? null,
            autoFillDateTime: field.autoFillDateTime ?? false,
          },
          createdBy,
          metadata: { formId, companyId, unitId, userUuid: createdBy },
        });
      }
    })
    .catch((err) => {
      toast({
        title: "Exclusão",
        position: "top-right",
        description: err.response.data.message || "Algo está errado tente novamente!",
        isClosable: false,
        status: "error",
        duration: 2500,
      });
      return err.response.data;
    });
};

/**
 * Obtém todos campos de um formulário.
 * @param {*} items
 * @returns
 */
export const allFieldsByName = async (items) => {
  const { formName } = items;
  return await getApiUrl
    .get(`/objetos/${formName}/campos/todos?withRef=true`)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.log(err);
      return null;
    });
};
/**
 * Obtém metadados de um formulário.
 * @param {*} items
 * @returns
 */
export const getMetadataOfObj = async (items) => {
  const { formName } = items;
  return await getApiUrl
    .get(`/objetos/${formName}/metadata/obter`)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.log(err);
      return null;
    });
};

/**
 * 
 * @param {*} items 
 * @returns 
 */
export const getQuantityFieldsNested = async (items) => {
  const { formName } = items;
  return await getApiUrl
    .get(`/objetos/${formName}/quantidade-edicoes`)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.log(err);
    });
}

/**
 * 
 * @param {*} items 
 * @returns 
 */
export const getReferencedFields = async (items) => {
  const { idField } = items;
  return await getApiUrl
    .get(`/get-all-incidence/${idField}`).then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.log(err);
    });
}

/**
 * Obtém campos de vários formulário.
 * @param {*} ids
 * @returns
 */
export const allFieldsByNames = async (ids) => {
  return await getApiUrl
    .post(`/objetos/formularios/campos/todos`, {
      ids,
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.log(err);
      return null;
    });
};

/**
 * Obtém campos de um formulário pelo seu id.
 * @param {*} formId
 * @returns
 */
export const allFieldsById = async (formId) => {
  return await getApiUrl
    .get(`/objetos/formularios/campos/todos/${formId}`)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.log(err);
      return null;
    });
};


/**
 * Exclui um formulario.
 * @param {*} table
 * @param {*} history
 * @returns
 */
export const dropObj = async (table, deletedBy, meta = {}) => {
  const { formId, companyId, unitId, fields = [] } = meta;
  return await getApiUrl
    .post(`/objetos/remover`, { table: table, deletedBy })
    .then(async (response) => {
      toast({
        title: "Exclusão",
        position: "top-right",
        description: "Formulário excluído com sucesso",
        isClosable: false,
        status: "success",
        duration: 1300,
      });

      const formNameClean = table.replace("obj_", "");
      // Notifica o update-service sobre os campos removidos junto com o form.
      if (Array.isArray(fields) && fields.length > 0) {
        await trackFormActivity({
          form: formNameClean,
          entity: "field",
          action: "deleted",
          payload: {},
          createdBy: deletedBy,
          metadata: { formId, companyId, unitId, userUuid: deletedBy },
          serviceUpdate: {
            type: "field_deleted",
            author: deletedBy,
            data: fields.map((f) => ({
              idField: f._id,
              field: f.field,
              fieldtype: f.fieldtype,
            })),
          },
        });
      }
      await trackFormActivity({
        form: formNameClean,
        entity: "form",
        action: "deleted",
        payload: {},
        createdBy: deletedBy,
        metadata: { formId, companyId, unitId, userUuid: deletedBy },
      });

      return response.data;
    })
    .catch((err) => {
      toast({
        title: "Exclusão",
        position: "top-right",
        description: err.response.data.message || "Algo está errado tente novamente!",
        isClosable: false,
        status: "error",
        duration: 2500,
      });
      return err.response.data;
    });
};

/**
 * 
 * @param {*} table 
 * @param {*} formName 
 * @param {*} fieldId 
 * @param {*} functionValidation 
 * @param {*} oldValidation 
 * @returns 
 */
export const editValidationObj = async (table, formName, fieldId, functionValidation, oldValidation) => {

  return await getApiUrl
    .post('/objetos/formularios/validacao/alterar', {
      table: table,
      formName: formName,
      fieldId: fieldId,
      functionValidation,
      oldValidation
    })
    .then((response) => {
      toast({
        title: "Edição",
        position: "top-right",
        description: response.data.message ||  "Função de validação editada com sucesso",
        isClosable: false,
        status: "success",
        duration: 3300,
      });
      return response.data;
    })
    .catch((err) => {
      toast({
        title: "Edição",
        position: "top-right",
        description: err.response.data.message || "Algo está errado tente novamente!",
        isClosable: false,
        status: "error",
        duration: 2500,
      });
      return err.response.data;
    });
}

/**
 * Edita o campo de um formulario.
 * @param {*} table
 * @param {*} info
 * @returns
 */
export const editfieldObj = async (table, info, updatedBy, meta = {}) => {
  return await getApiUrl
    .post(`/objetos/campos/editar`, {
      table: table,
      _id: info._id,
      index: info.index,
      field: info.field,
      checkBox: info.checkBox,
      refFields: info.refFields,
      refOpt: info.refOpt,
      getValueOf: info.getValueOf,
      checkValueIn: info.checkValueIn,
      isChanged: info.isChanged,
      oldProps: info.oldProps,
      dica: info.dica,
      sufixo: info.sufixo,
      type: info.fieldtype,
      autoFill: info.autoFill,
      marker: info.marker,
      requiredField: info.requiredField,
      updatedBy,
      formula: info.formula ?? "",
      returntype: info.returntype ?? "",
      autoFillDateTime: !!info.autoFillDateTime,
    })
    .then(async (response) => {
      toast({
        title: "Edição",
        position: "top-right",
        description: "Formulário editado com sucesso",
        isClosable: false,
        status: "success",
        duration: 3300,
      });
      const oldFieldName = info.oldProps?.field ?? null;
      await trackFormActivity({
        form: table.replace("obj_", ""),
        entity: "field",
        action: "updated",
        payload: {
          checkBox: info.checkBox,
          field: info.field,
          dica: info.dica,
          sufixo: info.sufixo,
          fieldtype: info.fieldtype,
          autoFill: info.autoFill,
          requiredField: info.requiredField,
          index: info.index,
          marker: info.marker,
          formula: info.formula ?? null,
          oldField: oldFieldName,
          autoFillDateTime: !!info.autoFillDateTime,
          // Snapshot do campo antes da edição, para exibir "antes → depois"
          // apenas nos atributos que realmente mudaram.
          previous: meta.previous ?? null,
        },
        createdBy: updatedBy,
        metadata: { formId: meta.formId, companyId: meta.companyId, unitId: meta.unitId, userUuid: updatedBy },
        serviceUpdateField: buildServiceUpdateField({
          author: updatedBy,
          idField: info._id,
          oldFieldName,
          newFieldName: info.field,
        }),
      });
      return response.data;
    })
    .catch((err) => {
      toast({
        title: "Edição",
        position: "top-right",
        description: err.response.data.message || "Algo está errado tente novamente!",
        isClosable: false,
        status: "error",
        duration: 2500,
      });
      return err.response.data;
    });
};

/**
 * Exclui campo de um formulario.
 * @param {*} table
 * @param {*} id
 * @param archivedBy
 * @returns
 */
export const dropObjField = async (table, id, archivedBy, fieldMeta = {}) => {
  return await getApiUrl
    .post(`/objetos/campos/remover`, {
      table: table,
      _id: id,
      isPermanent: true,
      archivedBy
    })
    .then(async (response) => {
      toast({
        title: "Exclusão",
        position: "top-right",
        description: "Campo excluído com sucesso.",
        isClosable: false,
        status: "success",
        duration: 2000,
      });
      await trackFormActivity({
        form: table.replace("obj_", ""),
        entity: "field",
        action: "archived",
        payload: { field: fieldMeta.field, fieldtype: fieldMeta.fieldtype },
        createdBy: archivedBy,
        metadata: {
          formId: fieldMeta.formId,
          companyId: fieldMeta.companyId,
          unitId: fieldMeta.unitId,
          userUuid: archivedBy,
        },
        serviceUpdate: {
          type: "field_deleted",
          author: archivedBy,
          data: [{ idField: id, field: fieldMeta.field, fieldtype: fieldMeta.fieldtype }],
        },
      });
      return response.data;
    })
    .catch((err) => {
      toast({
        title: "Exclusão",
        position: "top-right",
        description: err.response.data.message,
        isClosable: false,
        status: "error",
        duration: 2500,
      });
      return err.response.data;
    });
};

/**
 * Exclui campo arquivado de um formulario.
 * @param {*} table
 * @param {*} id
 * @param {*} history
 * @returns
 */
export const dropArchivedObjField = async (objects) => {
  const { formName, recordId, fields, deletedBy } = objects;
  return await getApiUrl
    .post(`/objetos/${formName}/metadata/adicionar`, {
      data: fields,
      isPermanent: true,
      _id: recordId,
      deletedBy
    })
    .then((response) => {
      toast({
        title: "Exclusão arquivados",
        position: "top-right",
        description: "Dados da coluna e campo excluído com sucesso.",
        isClosable: false,
        status: "success",
        duration: 1300,
      });
      return response.data;
    })
    .catch((err) => {
      toast({
        title: "Exclusão arquivados",
        position: "top-right",
        description: err.response.data.message,
        isClosable: false,
        status: "error",
        duration: 1300,
      });
      return err.response.data;
    });
};

/**
 * Altera a posição de exibição dos campos do objeto
 * @param {*} objects
 * @returns
 */
export const alterpos = async (objects) => {
  const { formName, fields, sortedBy, companyId, unitId, formId } = objects;
  return await getApiUrl
    .post(`/objetos/alterpos`, {
      formName,
      fields,
      sortedBy
    })
    .then(async (response) => {
      await trackFormActivity({
        form: formName.replace("obj_", ""),
        entity: "form",
        action: "sorted",
        payload: fields.map((item) => ({
          id: item._id,
          field: item.field,
          fieldtype: item.fieldtype,
          index: item.index,
        })),
        createdBy: sortedBy,
        metadata: { formId, companyId, unitId, userUuid: sortedBy },
      });
      return response.data;
    })
    .catch((err) => {
      toast({
        title: "Edição",
        position: "top-right",
        description: err.response.data.message,
        isClosable: false,
        status: "error",
        duration: 3300,
      });
      return err.response.data;
    });
};


/**
 * Obtém todos dados salvados em um ou mais objetos.
 * @param {*} forms
 * @returns
 */
export const savedDataInObj = async ({ forms, ids, useMatchAll, useBin }) => {
  return await getApiUrl
    .post(`/objetos/campos/todos/dados`, {
      forms,
      ids,
      useMatchAll,
      useBin,
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err;
    });
};

/**
 * Amostra dos registros mais recentes de um formulário, sem filtro.
 *
 * Alimenta a listagem de formulários (contagem, "último registro" e o painel de
 * estatísticas). Não existe endpoint agregado por unidade, então a listagem
 * chama isto por formulário, sob demanda — daí o `signal`, para cancelar o que
 * saiu da tela antes de responder.
 *
 * `showOnly` precisa listar explicitamente os campos desejados; sem eles o
 * retorno traz só createdAt/updatedAt, que é o suficiente para a contagem.
 * @returns { success, message: [registros], total, totalInIndex }
 */
export const sampleRecordsInObj = async ({ formName, pageSize = 1, showOnly = [], signal }) => {
  const response = await getApiUrl.post(
    `/objetos/formularios/dados/paginado?index=${formName}&page=1&pageSize=${pageSize}&isFilter=false`,
    {
      query: [],
      createdAt: null,
      showOnly: _.uniq([...showOnly, "createdAt", "updatedAt"]),
      sorter: [{ field: "createdAt", sort: "descend" }],
    },
    { signal }
  );
  return response.data;
};

/**
 * Cria categoria para formulário.
 * @param {*} items
 * @returns response
 */
export const addCategoryObj = async (items) => {
  const { categoryName, categoryColor, createdBy, unitId, companyId } = items;
  return await getApiUrl
    .post(`/objetos/categoria/nova`, {
      categoryName,
      categoryColor,
      unitId,
      companyId,
      createdBy
    })
    .then(async (response) => {
      if (response.data?.success) {
        await trackFormActivity({
          form: null,
          entity: "category",
          action: "created",
          payload: { categoryName, categoryColor },
          createdBy,
          metadata: { categoryId: response.data.categoryId, companyId, unitId, userUuid: createdBy },
        });
      }
      return response.data;
    })
    .catch((err) => {
      return err.response.data;
    });
};

/**
 * Busca por categoria do formulário existente.
 * @param {*} items
 * @returns
 */
export const findCategoryObj = async (items) => {
  const { id } = items;
  return await getApiUrl
    .get(`/objetos/categoria/${id}/pesquisar`)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.log(err);
      return null;
    });
};

/**
 * Edita categoria do formulário.
 * @param {*} objects
 * @returns
 */
export const editCategoryObj = async (objects) => {
  const { id, name, color, updatedBy, companyId, unitId } = objects;
  return await getApiUrl
    .post(`/objetos/categoria/editar/${id}`, {
      name,
      color,
      updatedBy
    })
    .then(async (response) => {
      if (!(response.data && response.data.success === false)) {
        await trackFormActivity({
          form: null,
          entity: "category",
          action: "updated",
          payload: { categoryName: name, categoryColor: color },
          createdBy: updatedBy,
          metadata: { categoryId: id, companyId, unitId, userUuid: updatedBy },
        });
      }
      return response.data;
    })
    .catch((err) => {
      return err.response?.data || { success: false };
    });
};

/**
 * Lista todas categorias dos formulários.
 * @returns
 */
export const allCategoriesObj = async ({ companyId, unitId }) => {
  try {
    const response = await getApiUrl.get('/objetos/categoria/todas', {
      params: {
        companyId,
        unitId
      }
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    throw error;
  }
};

/**
 * Remove categoria.
 * @param {*} objects
 * @returns
 */
export const removeCategoryObj = async (objects) => {
  const { _id, deletedBy, name, companyId, unitId } = objects;
  return await getApiUrl
    .post(`/objetos/categoria/remover`, {
      id: _id,
      deletedBy
    })
    .then(async (response) => {
      toast({
        title: "Edição",
        position: "top-right",
        description: "Categoria removida com sucesso",
        isClosable: false,
        status: "success",
        duration: 3300,
      });
      await trackFormActivity({
        form: null,
        entity: "category",
        action: "deleted",
        payload: { categoryName: name },
        createdBy: deletedBy,
        metadata: { categoryId: _id, companyId, unitId, userUuid: deletedBy },
      });
      return response.data;
    })
    .catch((err) => {
      console.log(err);
      return null;
    });
};

/**
 * Obtém todas categorias de um formulário.
 * @param {*} objects
 * @returns
 */
export const allCategoriesOfObj = async (objects) => {
  const { formName } = objects;
  return await getApiUrl
    .get(`/objetos/formularios/categorias/dados/${formName}`)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err.response.data;
    });
};

/**
 * Obtém todos formulario com categorias.
 * @returns
 * @param unitId
 */
export const allCategoriesWithObj = async (unitId) => {
  return await getApiUrl
    .get(`/objetos/formularios/categorias/dados/pesquisar/todos`, {
      params: {
        unitId
      }
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err.response.data;
    });
};

/**
 * Adiciona categoria em um formulário.
 * @param {*} objects
 * @returns
 */
export const pushCategoryInObj = async (objects) => {
  const { formName, categories, updatedBy, formId, companyId, unitId, categoriesData = [] } = objects;
  return await getApiUrl
    .post(`/objetos/formularios/categorias/push/${formName}`, {
      categories: categories,
      updatedBy
    })
    .then(async (response) => {
      toast({
        title: "Edição",
        position: "top-right",
        description: "Categoria adicionada com sucesso!",
        isClosable: false,
        status: "success",
        duration: 3300,
      });
      await trackFormActivity({
        form: formName.replace("obj_", ""),
        entity: "form",
        action: "updated",
        payload: {
          categories: categoriesData.map((c) => ({
            categoryName: c.categoryName,
            categoryId: c.categoryId,
            action: "push",
          })),
        },
        createdBy: updatedBy,
        metadata: { formId, companyId, unitId, userUuid: updatedBy },
      });
      return response.data;
    })
    .catch((err) => {
      toast({
        title: "Edição",
        position: "top-right",
        description: err.response.data.message,
        isClosable: false,
        status: "error",
        duration: 3300,
      });
      return err.response.data;
    });
};

/**
 * Remove categoria de um formulário.
 * @param {*} objects
 * @returns
 */
export const pullCategoryInObj = async (objects) => {
  const { formName, categories, updatedBy, formId, companyId, unitId, categoriesData = [] } = objects;
  return await getApiUrl
    .post(`/objetos/formularios/categorias/pull/${formName}`, {
      categories: categories,
      updatedBy
    })
    .then(async (response) => {
      toast({
        title: "Edição",
        position: "top-right",
        description: "Categoria removida com sucesso!",
        isClosable: false,
        status: "success",
        duration: 3300,
      });
      // Só registra atividade quando há autor (no fluxo de exclusão de
      // formulário o pull é interno e não deve gerar form_updated).
      if (updatedBy) {
        await trackFormActivity({
          form: formName.replace("obj_", ""),
          entity: "form",
          action: "updated",
          payload: {
            categories: categoriesData.map((c) => ({
              categoryName: c.categoryName,
              categoryId: c.categoryId,
              action: "pull",
            })),
          },
          createdBy: updatedBy,
          metadata: { formId, companyId, unitId, userUuid: updatedBy },
        });
      }
      return response.data;
    })
    .catch((err) => {
      return err.response.data;
    });
};

/**
 * Atualiza confirmação de um formulário.
 * @param {*} objects
 * @returns
 */
export const changeDescriptionInObj = async (objects) => {
  const { id, description, updatedBy, formName = null, companyId, unitId } = objects;
  return await getApiUrl
    .post(`/objetos/formularios/descricao/alterar`, {
      id,
      description,
      updatedBy
    })
    .then(async (response) => {
      toast({
        title: "Confirmação",
        position: "top-right",
        description: "Atualizado com sucesso!",
        isClosable: false,
        status: "success",
        duration: 3300,
      });
      await trackFormActivity({
        form: formName ? formName.replace("obj_", "") : null,
        entity: "form",
        action: "updated",
        payload: { description },
        createdBy: updatedBy,
        metadata: { formId: id, companyId, unitId, userUuid: updatedBy },
      });
      return response.data;
    })
    .catch((err) => {
      return err.response.data;
    });
};

/**
 * Atualiza sincronização de um formulário.
 * @param {*} objects
 * @returns
 */
export const changeConfigSyncInObj = async (objects) => {
  const { id, configSync, updatedBy, formName = null, companyId, unitId } = objects;
  return await getApiUrl
    .post(`/objetos/formularios/sincronizacao/alterar`, {
      id,
      configSync,
      updatedBy
    })
    .then(async (response) => {
      toast({
        title: "Confirmação",
        position: "top-right",
        description: "Atualizado com sucesso!",
        isClosable: false,
        status: "success",
        duration: 3300,
      });
      await trackFormActivity({
        form: formName ? formName.replace("obj_", "") : null,
        entity: "form",
        action: "updated",
        payload: { configSync },
        createdBy: updatedBy,
        metadata: { formId: id, companyId, unitId, userUuid: updatedBy },
      });
      return response.data;
    })
    .catch((err) => {
      return err.response.data;
    });
};

export const changeFieldDateMainInObj = async (objects) => {
  const { id, field, updatedBy, formName = null, companyId, unitId } = objects;
  return await getApiUrl
    .post(`/objetos/formularios/data-principal/alterar`, {
      id,
      field,
      updatedBy
    })
    .then(async (response) => {
      toast({
        title: "Confirmação",
        position: "top-right",
        description: "Atualizado com sucesso!",
        isClosable: false,
        status: "success",
        duration: 3300,
      });
      await trackFormActivity({
        form: formName ? formName.replace("obj_", "") : null,
        entity: "form",
        action: "updated",
        payload: { fieldDateMain: field },
        createdBy: updatedBy,
        metadata: { formId: id, companyId, unitId, userUuid: updatedBy },
      });
      return response.data;
    })
    .catch((err) => {
      return err.response.data;
    });
}

/**
 * Adiciona registro
 * @param {*} objects
 * @returns
 */
export const addRecords = async (objects) => {
  const { formName, record, records, createdBy, acceptEmptyValues = false } = objects;

  const payload = {
    formName,
    createdBy,
    acceptEmptyValues
  };

  if (records) {
    payload.records = records;
  } else if (record) {
    payload[`record${(_.isArray(record)) ? 's' : ''}`] = record;
  }

  return await getApiUrl
    .post(
      `/objetos/formularios/dados/novo?device=web&network=online`,
      payload
    )
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      throw err;
    });
};

/**
 * Restaura registros da lixeira
 * @param {*} objects
 * @returns
 */
export const restoreRecords = async (objects) => {
  const { formName, ids, createdBy } = objects;
  return await getApiUrl
    .post(
      `/objetos/formularios/dados/restaurar?device=web&network=online`,
      {
        formName,
        ids,
        createdBy,
      }
    )
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err;
    });
};

/**
 * Atualiza registros salvos
 * @param {*} objects
 * @returns
 */
export const updateRecords = async (objects) => {
  const { formName, ids, records, forRemove, createdBy } = objects;
  return await getApiUrl
    .post(
      `/objetos/formularios/dados/alterar?device=web&network=online`,
      {
        formName,
        ids,
        records,
        forRemove,
        createdBy,
      }
    )
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      throw err;
    });
};

/**
 * Atualiza registros salvos
 * @param {*} objects
 * @returns
 */
export const deleteRecords = async (objects) => {
  const { formName, ids, records, useBin, isObjDataDelete, createdBy } =
    objects;
  return await getApiUrl
    .post(
      `/objetos/formularios/dados/remover?device=web&network=online`,
      {
        formName,
        ids,
        useBin,
        isObjDataDelete,
        createdBy,
      }
    )
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err;
    });
};

/**
 * Adiciona novo hit na coleçāo
 * @param {*} objects
 * @returns
 */
export const addHitByObj = async (objects) => {
  const { index, id } = objects;
  return await getApiUrl
    .post(`/objetos/buscador/novo/hit`, {
      index,
      id,
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err;
    });
};

/**
 * Adiciona formulário como favorito
 * @param {*} objects
 * @returns
 */
export const pushObjInFavorite = async (objects) => {
  const { formId, userId, type } = objects;
  return await getApiUrl
    .post(`/objetos/formularios/favoritos/push/${formId}`, {
      userId,
      type,
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err;
    });
};


/**
 * Adiciona formulário como favorito
 * @param {*} objects
 * @returns
 */
export const pullObjInFavorite = async (objects) => {
  const { formId, userId, type } = objects;
  return await getApiUrl
    .post(`/objetos/formularios/favoritos/pull/${formId}`, {
      userId,
      type,
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err;
    });
};

/**
 * Pesquisa em todos os formulários
 * @param {*} objects
 * @returns
 */
export const searchAllByObj = async (objects) => {
  const { q, companyId, unitId } = objects;
  return await getApiUrl
    .post(`/objetos/buscador/formularios/todos`, {
      q,
      companyId,
      unitId
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err;
    });
};

export const getRefFieldsById = async ({ fields }) => {
  return await getApiUrl
    .post(`/objetos/campos/pesquisar/id`, {
      fields,
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.log(err);
      return null;
    });
};

export const getRefFieldsByData = async ({ fields, withCreatedAt, onlyCreatedAt }) => {
  return await getApiUrl
    .post(`/objetos/buscador/dados/campos`, {
      fields,
      withCreatedAt,
      onlyCreatedAt,
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      console.log(err);
      return null;
    });
};

/**
 * Obtém detalhes de um registro
 * com nome do form e id do registro
 * @param {*} param0
 * @returns
 */
export const getFieldsDataById = async ({ table, id }) => {
  return await getApiUrl
    .post(`/objetos/buscador/formularios/dados/detalhes`, {
      table,
      id,
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err;
    });
};

/**
 * Obtém detalhes de um registro
 * com nome id do registro
 * @param {*} param0
 * @returns
 */
export const getFieldsDataByDocId = async ({ id, viewAsField }) => {
  return await getApiUrl
    .post(`/objetos/buscador/formularios/registro/detalhes`, {
      id,
      viewAsField,
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err;
    });
};

/**
 * Obtém detalhes de registro
 * com lista de ids
 * @param {*} ids
 * @param viewAsField
 * @returns
 */
export const getFieldsDataByDocIds = async ({ ids, viewAsField }) => {
  return await getApiUrl
    .post(`/objetos/buscador/formularios/registros/detalhes`, {
      ids,
      viewAsField,
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err;
    });
};

/**
 * Verifica a opçāo selecionada no campo lookup
 * @param {*} param0
 * @returns
 */
export const getFieldsDataByLookup = async ({ table, id, fieldId, checkIn }) => {
  return await getApiUrl
    .post(
      `/objetos/buscador/formularios/dados/lookup/detalhes`,
      {
        table,
        id,
        fieldId,
        checkIn,
      },
      // Evita que uma requisição sem resposta do servidor trave o
      // carregamento dos campos de retorno (spinner infinito).
      { timeout: 30000 }
    )
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err;
    });
};

/**
 * Verifica a opçāo selecionada no campo lookup
 * @returns
 * @param data
 */
export const verifyCanDeleteField = async (data = {}) => {
  return await getApiUrl
    .post(`/objetos/campos/remove/verify`, data)
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err;
    });
};

export const getPreviewValueFormula = async (objects) => {
  const { formName, idFieldFormula, valueFormula = null, typePreview, fakeFields, idRecord = null } = objects;
  return await getApiUrl
    .post("/objetos/formula/visualizar-valor",
      {
        formName,
        idFieldFormula,
        idRecord,
        valueFormula,
        typePreview,
        fakeFields
      }).then((response) => {
        return response.data;
      }).catch((err) => {
        return err?.response?.data ?? "Erro ao buscar pré-visualização do campo fórmula."
      });
};
